import { useState, useEffect } from "react";
import { IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Button } from "./components/common/Button";
import { FormField, TextInput } from "./components/common/FormInput";
import CustomSelect from "../CommonComponents/Map/CustomerDropdown";
import { INQUIRY_TYPE_OPTIONS } from "../../utils/fieldsConfig";
import { canAssignStaff } from "../../utils/commonutils";
import { defaultInquiryType, getAllowedInquiryTypes } from "../../utils/inquiryWorkstream";
import { RightDrawer, ModalHeader, ModalBody, ModalFooter } from "../CommonComponents/RightDrawer";
import MasterSelect from "../CommonComponents/MasterSelect";
import {
  FALLBACK_INQUIRY_PRIORITY,
  FALLBACK_INQUIRY_SERVICE,
  FALLBACK_INQUIRY_SOURCE,
  FALLBACK_INQUIRY_STATUS,
  FALLBACK_INQUIRY_TYPE,
} from "../../utils/masterFallbacks";
import { useMasters } from "../../hooks/useMasters";

const empty = {
  name: "",
  phone: "",
  service: "",
  source: "Website",
  priority: "Low",
  assignedTo: "",
  companyName: "",
  password: "",
  note: "",
  type: "general",
  status: "new",
};

export default function AddEditInquiryModal({
  open,
  onClose,
  onSubmit,
  editData,
  mode = "inquiry",
  defaultType,
  employeeData = [],
  props,
}) {
  const sourceOptions = useMasters("inquiry_source", FALLBACK_INQUIRY_SOURCE);
  const isEdit = Boolean(editData);

  const [values, setValues] = useState(empty);
  const [errors, setErrors] = useState({});
  const [otherSource, setOtherSource] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [step, setStep] = useState(1);

  useEffect(() => {
    if (editData) {
      const sourceValues = sourceOptions.map((item) => item.value);
      const isCustomSource = editData.source && !sourceValues.includes(editData.source);
      setValues({
        ...empty,
        name: editData.name || "",
        phone: editData.mobile || "",
        service: editData.service || "",
        source: isCustomSource ? "Other" : editData.source || "Website",
        priority: editData.priority || "Low",
        assignedTo: editData?.assignedTo?._id || "",
        note: editData.notes || "",
        type: editData.type || "general",
        status: editData.status || "new",
      });

      if (isCustomSource) {
        setOtherSource(editData.source || "");
      }
    } else {
      setValues({
        ...empty,
        type: defaultType || defaultInquiryType(props?.authData?.role),
      });
      setOtherSource("");
    }
    setStep(1);
  }, [editData, mode, open, defaultType]);

  if (!open) return null;

  const set = (key) => (e) => {
    const value = e.target.value;

    setValues((v) => ({
      ...v,
      [key]: value,
    }));

    setErrors((er) => ({
      ...er,
      [key]: "",
    }));

    if (key === "source") {
      setOtherSource("");
      setErrors((er) => ({
        ...er,
        otherSource: "",
      }));
    }
  };

  const validate = () => {
    const e = {};

    if (!values.name) e.name = "Name required";

    if (!values.phone) {
      e.phone = "Mobile required";
    } else if (!/^\d{10}$/.test(values.phone)) {
      e.phone = "Enter valid 10 digit number";
    }

    if (!values.assignedTo && canAssignStaff(props?.authData?.role)) {
      e.assignto = "Assigned user is required";
    }

    if (values.source === "Other" && !otherSource) {
      e.otherSource = "Please specify source";
    }

    if (mode === "convert") {
      if (!values.companyName) e.companyName = "Company name required";
      const passwordRegex =
        /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

      if (!values.password) {
        e.password = "Password required";
      } else if (!passwordRegex.test(values.password)) {
        e.password =
          "Password must be 8+ chars, include uppercase, lowercase, number & special character";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
      return;
    }
    if (!validate()) return;

    const finalSource = values.source === "Other" ? otherSource : values.source;

    let status = values.status || "new";
    if (mode === "convert") {
      status = "converted";
    }

    if (mode === "convert") {
      onSubmit({
        ...values,
        name: values.name,
        mobile: values.phone,
        password: values.password,
        companyName: values.companyName,
        employeeId: values.assignedTo,
        source: finalSource,
        note: values.note,
        status,
      });
    } else {
      onSubmit({
        ...values,
        source: finalSource,
        note: values.note,
        status,
      });
    }
    onClose();
  };
  const handleClose = () => {
    setValues(empty);
    setErrors({});
    setOtherSource("");
    setStep(1);
    onClose();
  };

  return (
    <RightDrawer isOpen={open} onClose={handleClose}>
      <ModalHeader toggle={handleClose} kicker="Inquiry">
        {mode === "convert"
          ? "Convert to customer"
          : isEdit
            ? "Edit inquiry"
            : "Add inquiry"}
      </ModalHeader>
      <ModalBody>
        <div className="step-tabs">
          <button type="button" className={`step-tab ${step === 1 ? "active" : ""}`} onClick={() => setStep(1)}>
            1. Contact
          </button>
          <button type="button" className={`step-tab ${step === 2 ? "active" : ""}`} onClick={() => setStep(2)}>
            2. Details
          </button>
        </div>
        <form id="inquiry-form" onSubmit={handleSubmit} className="space-y-4">
          {step === 1 && (
            <div className="grid gap-4">
              <FormField label="Full name" required error={errors.name}>
                <TextInput value={values.name} onChange={set("name")} placeholder="Enter full name" />
              </FormField>
              <FormField label="Mobile" required error={errors.phone}>
                <TextInput value={values.phone} onChange={set("phone")} placeholder="10-digit mobile" type="number" />
              </FormField>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label="Source">
                  <MasterSelect
                    group="inquiry_source"
                    fallback={FALLBACK_INQUIRY_SOURCE}
                    value={values.source}
                    onChange={set("source")}
                  />
                </FormField>
                <FormField label="Inquiry type">
                  <MasterSelect
                    group="inquiry_type"
                    fallback={INQUIRY_TYPE_OPTIONS.length ? INQUIRY_TYPE_OPTIONS : FALLBACK_INQUIRY_TYPE}
                    value={values.type}
                    onChange={set("type")}
                    allowedValues={(getAllowedInquiryTypes(props?.authData?.role) || ["general", "payment_recovery", "task", "gst"]).filter(
                      (value) => value !== "case_management" || values.type === "case_management"
                    )}
                  />
                </FormField>
              </div>
              {values.source === "Other" && (
                <FormField label="Specify source" error={errors.otherSource}>
                  <TextInput value={otherSource} onChange={(e) => setOtherSource(e.target.value)} placeholder="Enter source" />
                </FormField>
              )}
            </div>
          )}
          {step === 2 && (
            <div className="grid gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label="Service">
                  <MasterSelect
                    group="inquiry_service"
                    fallback={FALLBACK_INQUIRY_SERVICE}
                    value={values.service}
                    placeholder="Select service"
                    onChange={set("service")}
                  />
                </FormField>
                <FormField label="Priority">
                  <MasterSelect
                    group="inquiry_priority"
                    fallback={FALLBACK_INQUIRY_PRIORITY}
                    value={values.priority}
                    onChange={set("priority")}
                  />
                </FormField>
                {mode !== "convert" && (
                  <FormField label="Status">
                    <MasterSelect
                      group="inquiry_status"
                      fallback={FALLBACK_INQUIRY_STATUS}
                      value={values.status}
                      onChange={set("status")}
                    />
                  </FormField>
                )}
              </div>
              <FormField label="Notes">
                <TextInput value={values.note} onChange={set("note")} placeholder="Optional notes" />
              </FormField>
              {mode === "convert" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField label="Company name" required error={errors.companyName}>
                    <TextInput value={values.companyName} onChange={set("companyName")} placeholder="Company name" />
                  </FormField>
                  <FormField label="Password" required error={errors.password}>
                    <div style={{ position: "relative" }}>
                      <TextInput
                        type={showPassword ? "text" : "password"}
                        value={values.password}
                        onChange={set("password")}
                        placeholder="Set password"
                        style={{ paddingRight: 40 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label="toggle password visibility"
                        sx={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)" }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </div>
                  </FormField>
                </div>
              )}
              <FormField label="Assign to" error={errors.assignto}>
                <CustomSelect
                  value={values.assignedTo}
                  onChange={set("assignedTo")}
                  disabled={!canAssignStaff(props?.authData?.role)}
                >
                  <option value="">Unassigned</option>
                  {employeeData
                    ?.filter((e) => e?.userId?._id)
                    .map((e) => (
                      <option key={e.userId._id} value={e.userId._id}>
                        {e.name}
                      </option>
                    ))}
                </CustomSelect>
              </FormField>
            </div>
          )}
        </form>
      </ModalBody>
      <ModalFooter>
        {step === 1 ? (
          <>
            <Button variant="ghost" type="button" onClick={handleClose}>Cancel</Button>
            <Button type="button" onClick={() => setStep(2)}>Next</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
            <Button type="submit" form="inquiry-form">
              {mode === "convert" ? "Convert" : isEdit ? "Save changes" : "Create inquiry"}
            </Button>
          </>
        )}
      </ModalFooter>
    </RightDrawer>
  );
}
